import { getDirectApi } from 'mytours-read-common'
import { offlineSupported, workerReady } from '../../sw/register'
import stqryBunnyReadApi from '../api/stqryBunnyReadApi'
import { getCdnApiBaseUrl, getDeviceUUID, getPlatform } from '../platform'
import { getAccountId, getProjectCredentials, shouldUsePublishedContent } from '../settings/functions'
import db from '../db'
import { DexieDownload, DownloadState } from '../db/types'
import { attachDownload, beginDownload, endDownload, reportProgress } from './active'
import { routingCache } from './cache'
import { contentFetch, ContentLocator, directLocator, publishedLocator } from './locator'
import {
	collectionKey,
	Download,
	DownloaderDeps,
	downloadCollection,
	DownloadRows,
	downloadProjectCacheSet,
	PROJECT_KEY,
	refreshDownloads,
	removeDownload,
	retryDownload,
} from './downloader'

export { activeDownload, activeDownloadKeys, subscribeToDownloads } from './active'
export type { ActiveDownload } from './active'
export { collectionKey, PROJECT_KEY } from './downloader'
export type { DownloaderDeps } from './downloader'
export { offlineSupported } from '../../sw/register'

const rows: DownloadRows = {
	all: () => db.downloads.toArray(),
	get: (key: string) => db.downloads.get(key),
	put: (row: DexieDownload) => db.downloads.put(row).then(() => undefined),
	remove: (key: string) => db.downloads.delete(key),
}

export interface OfflineOptions {
	accountId: number
	language: string
}

/**
 * Where this project's content lives. A project with the publishing model on reads the published subtree;
 * the large majority, which have it off, read the direct API — the same split browsing already makes.
 */
export const contentLocator = (): ContentLocator => {
	if (shouldUsePublishedContent()) {
		return publishedLocator(stqryBunnyReadApi)
	}

	const { projectId, apiKey } = getProjectCredentials()

	return directLocator({
		baseUrl: getCdnApiBaseUrl(),
		devicePlatform: getPlatform(),
		deviceUdid: getDeviceUUID(),
		projectId,
		apiKey,
	})
}

/**
 * Refetch what a launch boots from, now through the worker so it keeps a copy: the launch that installed the
 * worker fetched them before it could.
 */
export const saveLaunchCopies = async (): Promise<void> => {
	const requests = shouldUsePublishedContent()
		? [stqryBunnyReadApi.fetchApp(getAccountId()), stqryBunnyReadApi.fetchManifest(getAccountId())]
		: [
				getDirectApi().appShow(getPlatform(), getDeviceUUID()),
				getDirectApi().appManifest(getPlatform(), getDeviceUUID()),
			]

	await Promise.allSettled(requests)
}

export const offlineDeps = (
	{ accountId, language }: OfflineOptions,
	onProgress?: DownloaderDeps['onProgress'],
	title?: string,
): DownloaderDeps => {
	const api = contentLocator()

	return {
		api,
		accountId,
		language,
		devicePixelRatio: window.devicePixelRatio || 1,
		cache: routingCache(window.caches),
		fetch: contentFetch(api.contentOrigin, getProjectCredentials()),
		workerReady: () => workerReady().then(saveLaunchCopies),
		rows,
		onProgress,
		title,
	}
}

/**
 * Ask the browser to keep what is downloaded. Storage a visitor asked for is worth asking not to evict,
 * and a refusal is not a reason to skip the download — it only makes eviction likelier.
 */
export const requestPersistence = async (): Promise<boolean> => {
	try {
		return (await navigator.storage?.persist?.()) ?? false
	} catch {
		return false
	}
}

/**
 * Run a download under one key, tracked so any page can show its progress and stop it. Only one runs per
 * key: the download is owned by the registry rather than by the page that started it, so navigating away,
 * backgrounding or finishing the tour leaves it running, and coming back finds it rather than a second one.
 */
const run = async (key: string, start: (deps: DownloaderDeps) => Promise<Download>, title?: string): Promise<void> => {
	const chosen = options
	if (!chosen || !offlineSupported() || !beginDownload(key)) return

	try {
		await requestPersistence()

		const download = await start(offlineDeps(chosen, progress => reportProgress(key, progress), title))
		attachDownload(key, { cancel: () => download.queue.cancel(), prioritizeStops: download.prioritizeStops })

		await download.done
	} catch {
		// Offline support is additive: a download that will not start must not stop the app working.
	} finally {
		endDownload(key)
	}
}

/**
 * The account and language every download runs under. Unset until the visitor has chosen a language, and
 * nothing downloads before then: every published URL carries a language, so a download started earlier is
 * a download of the wrong content.
 */
let options: OfflineOptions | undefined

export const setOfflineOptions = (next: OfflineOptions): void => {
	options = next
}

export const startCollectionDownload = (collectionId: number, title?: string): Promise<void> =>
	run(collectionKey(collectionId), deps => downloadCollection(collectionId, deps), title)

export const startProjectDownload = (): Promise<void> => run(PROJECT_KEY, deps => downloadProjectCacheSet(deps))

export const retry = async (key: string): Promise<DownloadState | undefined> => {
	const chosen = options
	if (!chosen || !offlineSupported() || !beginDownload(key)) return undefined

	try {
		return await retryDownload(
			key,
			offlineDeps(chosen, progress => reportProgress(key, progress)),
		)
	} catch {
		return undefined
	} finally {
		endDownload(key)
	}
}

export const remove = async (key: string): Promise<void> => {
	const chosen = options
	if (!chosen || !offlineSupported()) return

	try {
		await removeDownload(key, offlineDeps(chosen))
	} catch {
		// A removal that will not run leaves the row where it was, which the storage row reports honestly.
	}
}

/**
 * Bring downloaded tours up to date once the visitor's language is known. Silent by design: nothing is
 * prompted, nothing is blocked, and with no connection every row is left exactly as it was.
 */
export const refreshOnLaunch = async (): Promise<void> => {
	if (!offlineSupported() || !navigator.onLine || !options) return

	try {
		await refreshDownloads(offlineDeps(options))
	} catch {
		// Offline support is additive: a refresh that will not run must not stop the app working.
	}
}
